import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    GoogleLogo,
    CheckCircle,
    ArrowClockwise,
    Plug,
    PlugsConnected,
    Lightning,
    FilePdf,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

/**
 * Manual-import runner for the Drive Scanner.
 * Same UX as MailConnector's IMAP `ImportRunner`:
 *  - "Tester la lecture" → /scanner/probe (counts PDFs in FACTURE A TRAITER)
 *  - "Démarrer l'import" → /scanner/harvest (background job)
 *  - Live status polling via /sync/current with progress %
 */
function ImportRunner({ connected }) {
    const [running, setRunning] = useState(false);
    const [status, setStatus] = useState(null);
    const [testing, setTesting] = useState(false);
    const [probe, setProbe] = useState(null);

    const testRead = async () => {
        setTesting(true);
        setProbe(null);
        try {
            const { data } = await api.post("/scanner/probe");
            setProbe(data);
            if (data.exists) {
                toast.success(`${data.total} PDF dans « ${data.folder} »`);
            } else {
                toast.warning(data.message);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec du test");
        } finally {
            setTesting(false);
        }
    };

    const runImport = async () => {
        setRunning(true);
        setStatus(null);
        try {
            const { data } = await api.post("/scanner/harvest");
            toast.info("Scan Google Drive lancé");
            setStatus({ id: data.id, message: data.message, progress: data.progress });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec du lancement");
            setRunning(false);
        }
    };

    // Poll status while job running (mirrors MailConnector behavior)
    useEffect(() => {
        if (!status?.id) return;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get("/sync/current");
                if (data) {
                    setStatus(data);
                    if (data.status === "success" || data.status === "failed" || data.status === "error") {
                        setRunning(false);
                        return;
                    }
                }
            } catch (e) {
                // ignore
            }
            setTimeout(poll, 2500);
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [status?.id]);

    return (
        <Card data-testid="scanner-import-runner-card">
            <CardContent className="p-6 space-y-4">
                <div>
                    <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                        <Lightning size={18} weight="duotone" />
                        Lancer l&apos;import
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Récupère les PDF déposés dans le dossier{" "}
                        <code className="bg-muted px-1.5 py-0.5 rounded">FACTURE A TRAITER</code> de votre Drive.
                    </p>
                </div>
                {!connected ? (
                    <div className="text-sm text-muted-foreground bg-muted/40 rounded-md px-4 py-3">
                        Connectez d&apos;abord votre Google Drive au-dessus.
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            onClick={runImport}
                            disabled={running}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="scanner-run-import"
                        >
                            <ArrowClockwise size={16} className="mr-2" />
                            {running ? "Import en cours…" : "Démarrer l'import"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={testRead}
                            disabled={testing || running}
                            data-testid="scanner-test-read"
                        >
                            {testing ? "Test…" : "Tester la lecture"}
                        </Button>
                    </div>
                )}
                {probe && (
                    <div
                        className="border border-border rounded-md p-3 bg-muted/30 text-sm space-y-2"
                        data-testid="scanner-probe-result"
                    >
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-semibold">{probe.total} PDF</span>
                            <span className="text-muted-foreground">·</span>
                            <span className="text-muted-foreground">
                                dossier <code className="bg-muted px-1 py-0.5 rounded">{probe.folder}</code>
                            </span>
                            {!probe.exists && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium">
                                    Dossier manquant
                                </span>
                            )}
                        </div>
                        {probe.samples?.length > 0 && (
                            <div className="space-y-0.5">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                    Prochains PDFs à traiter
                                </div>
                                {probe.samples.map((f, i) => (
                                    <div
                                        key={i}
                                        className="text-xs py-1 border-t border-border/60 flex items-center gap-2"
                                    >
                                        <FilePdf size={14} className="text-rose-500 shrink-0" />
                                        <span className="truncate flex-1">{f.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {status && (
                    <div
                        className="border-l-2 border-primary pl-3 py-1 text-sm"
                        data-testid="scanner-status"
                    >
                        <div className="font-medium">{status.message || status.status}</div>
                        {typeof status.progress === "number" && status.progress < 100 && (
                            <div className="text-xs text-muted-foreground mt-1">
                                Progression : {status.progress}%
                            </div>
                        )}
                        {status.invoices_found > 0 && (
                            <div className="text-xs text-emerald-700 mt-1">
                                {status.invoices_found} factures importées
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function ScannerConnector() {
    const [params] = useSearchParams();
    const { data: status, mutate } = useSWR("/scanner/status", fetcher);
    const { data: jobs } = useSWR("/sync-jobs?provider=scanner&limit=5", fetcher);

    useEffect(() => {
        if (params.get("connected") === "1") {
            toast.success("Google Drive connecté !");
        }
    }, [params]);

    const connect = async () => {
        try {
            const { data } = await api.get("/scanner/google/connect");
            window.location.href = data.authorization_url;
        } catch (e) {
            toast.error(
                e.response?.data?.detail || "Connexion impossible — vérifier la configuration serveur"
            );
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Déconnecter Google Drive ?")) return;
        await api.delete("/scanner/disconnect");
        toast.success("Google Drive déconnecté");
        await mutate();
    };

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-6">
            <div>
                <h1
                    className="font-display text-3xl font-bold tracking-tight"
                    data-testid="scanner-page-title"
                >
                    Connecteur Scanner
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Récupère automatiquement les factures (PDF) depuis un dossier Google Drive.
                </p>
                <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-300 text-xs text-emerald-800">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Synchronisation automatique activée — toutes les heures
                </div>
            </div>

            <Card>
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-start gap-3">
                        <GoogleLogo size={32} weight="duotone" className="text-primary mt-1" />
                        <div className="flex-1">
                            <h3 className="font-display text-lg font-bold">Google Drive</h3>
                            {status?.connected ? (
                                <div className="flex items-center gap-2 mt-1 text-sm text-emerald-700">
                                    <CheckCircle size={16} weight="fill" />
                                    Connecté
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground mt-1">Non connecté</p>
                            )}
                        </div>
                        {status?.connected ? (
                            <Button variant="outline" onClick={disconnect} data-testid="scanner-disconnect">
                                <PlugsConnected size={16} className="mr-2" />
                                Déconnecter
                            </Button>
                        ) : (
                            <Button onClick={connect} data-testid="scanner-connect">
                                <Plug size={16} className="mr-2" />
                                Connecter Google Drive
                            </Button>
                        )}
                    </div>

                    {status?.connected && (
                        <div className="border-t pt-4 text-sm space-y-1">
                            <p className="text-muted-foreground">
                                Le scanner cherche les PDF dans le dossier <b>FACTURE A TRAITER</b> de votre
                                Drive, classe chaque PDF, puis déplace selon son type :
                            </p>
                            <ul className="list-disc list-inside text-muted-foreground pl-2">
                                <li>
                                    <b>Factures</b> et <b>avoirs</b> → <b>TERMINER</b>
                                </li>
                                <li>
                                    <b>Bons de livraison</b> → <b>BON DE LIVRAISON TERMINER</b>
                                </li>
                                <li>
                                    <b>Bons d&apos;intervention</b> → <b>BON INTER TERMINE</b>
                                </li>
                                <li>
                                    <b>Devis acceptés</b> → <b>DEVIS TERMINER</b>
                                </li>
                                <li>
                                    <b>Devis non acceptés / autres / non lisibles</b> → <b>NON TRAITE</b>
                                </li>
                            </ul>
                            <p className="text-xs text-amber-700 mt-2">
                                💡 Créez le dossier <b>FACTURE A TRAITER</b> à la racine de votre Drive et
                                déposez-y vos PDFs. Les dossiers de destination seront créés
                                automatiquement au premier scan.
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            <ImportRunner connected={!!status?.connected} />

            {jobs && jobs.length > 0 && (
                <Card>
                    <CardContent className="p-6">
                        <h3 className="font-display text-lg font-bold mb-3">Historique des scans</h3>
                        <ul className="space-y-2 text-sm">
                            {jobs.map((j) => (
                                <li key={j.id} className="flex justify-between border-b pb-2">
                                    <span>{new Date(j.created_at).toLocaleString("fr-FR")}</span>
                                    <span className="text-muted-foreground">{j.message}</span>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
